import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertLineURLDTO } from "../../../../models/alert-line-url/alert-line-url-dto";
import { AlertLineReportSTO } from "../../../../models/alert-line-report/alert-line-report-sto";
import { v4 as uuid } from "uuid";
import {
  AddressedToContainer,
  AddressToField,
  Container,
  DeleteFileButton,
  DescriptionBlockContainer,
  DescriptionContainer,
  DescriptionLabelContainer,
  DocumentViewerContainer,
  Field,
  Label,
  RecordedAudioContainer,
  RecordedAudioList,
  ReportTypeContainer,
  SingleRecordedAudioContainer,
  SingleUploadedFile,
  SmallLabel,
  UploadedFilesContainer,
  UploadFilesContainer,
} from "./styles";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogContent,
  useTheme,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  TextField,
  Typography,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { ViolationClassificationApiService } from "../../../../services/violation-classification/violation-classification-api.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IFile } from "../../../../reusable-components/DocumentViewer/interfaces";
import { FileIconButton } from "../../../../containers/AttachedFiles/styles";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { customFileIconStyles } from "../../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "../../../../reusable-components/DocumentViewer/DocumentViewer";
import { getFileExtension } from "../../../../utils/files-utils/get-file-extension";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import VoiceRecorder from "../../../../containers/VoiceRecorder/VoiceRecorder";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { AttachIcon } from "../../../../assets/icons";
import { AlertLineReportApiService } from "../../../../services/alert-line/alert-line-report-api.service";
import { AlertLineReportStatusRef } from "../../../../models/alert-line-report-status/alert-line-report-status-ref";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { ALERT_LINE_PAGE } from "../AlertLinePage";
import { convertBlobToBase64 } from "../../../../utils/files-utils/convert-file";
import { AlertLineReportFileRef } from "../../../../models/alert-line-report/alert-line-report-file-ref";
import {
  acceptedTextFileExtensions,
  acceptedImageFileExtensions,
  acceptedAudioFileExtensions,
  acceptedVideoFileExtensions,
} from "./accepted-file-extensions";
import { checkFileMimeType } from "../../../../utils/files-utils/check-file-mime-type";
import { AlertLineReportDTO } from "../../../../models/alert-line-report/alert-line-report-dto";
import { AlertLineReportRef } from "../../../../models/alert-line-report/alert-line-report-ref";
import { AlertLineURLLanguageVersionRef } from "../../../../models/alert-line-url/alert-line-url-language-version-ref";
import PostSubmitAlertLineReport from "./PostSubmitAlertLineReport";
import { ViolationClassificationFilter } from "../../../../models/violation-classification/violation-classification-filter";
import { AlertLineURLRef } from "../../../../models/alert-line-url/alert-line-url-ref";
import { saveDocs } from "./utils";
import { handleUploadNewFiles } from "../../../../containers/Document/Document/utils";

interface Props {
  alertLineURL: AlertLineURLDTO;
  handleChangeView: (
    view: ALERT_LINE_PAGE,
    previousReport?: AlertLineReportDTO,
    requiresConfirmation?: boolean
  ) => void;
  selectedLanguage?: AlertLineURLLanguageVersionRef;
}

export interface CustomFiles {
  alertLineReport: AlertLineReportRef | null;
  fileName: string;
  fileType: string;
  file: string;
  isDescription: boolean;
  isFileMalicious: boolean;
  isDeleted: boolean;
  id: string;
  blob: File;
  audioSrc?: string;
}

interface VocalDescriptionURL {
  url: string;
  fileId: string;
}

interface State {
  loading: boolean;
  error: string;
  alertLineReport: AlertLineReportSTO;
  files: IFile[];
  fileToView: IFile | null;
  vocalDescriptionURL: VocalDescriptionURL[];
  dialogKeyOpen: boolean;
  allFieldsValidated: boolean;
  exportLoading: boolean;
  isPostSubmitView: boolean;
  filesToStore: CustomFiles[];
}

const AddAlertLineReport = ({ alertLineURL, handleChangeView, selectedLanguage }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.alert_line_report.add_alert_line_report";
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const DEFAULT_ITEM: AlertLineReportSTO = {
    key: uuid(),
    id: uuid(),
    isDeleted: false,
    alertLineURL: { id: alertLineURL.id } as AlertLineURLRef,
    alertLineReportStatus: { id: "73ec794c-1cb9-4b43-a6ba-e585453e54bb" } as AlertLineReportStatusRef,
    subject: "",
    violationClassification: null,
    description: "",
    anonymous: true,
    fullName: "",
    phoneNumber: "",
    vocalDescription: "",
    addressedToHr: false,
    addressedToManagement: false,
    addressedToCompanyCommittee: false,
    files: [],
    email: "",
    language: selectedLanguage?.language ?? null,
  };

  const INITIAL_STATE: State = {
    loading: false,
    error: "",
    alertLineReport: DEFAULT_ITEM,
    files: [],
    fileToView: null,
    vocalDescriptionURL: [],
    dialogKeyOpen: false,
    allFieldsValidated: false,
    exportLoading: false,
    isPostSubmitView: false,
    filesToStore: [],
  };

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change
  const handleChange = useCallback(
    (field: keyof AlertLineReportSTO, value: AlertLineReportSTO[keyof AlertLineReportSTO]) => {
      setState((state) => ({
        ...state,
        alertLineReport: {
          ...state.alertLineReport,
          [field]: value,
          ...(field === "anonymous" &&
            value === false && {
              fullName: "",
              phoneNumber: "",
              email: "",
            }),
        },
      }));
    },
    []
  );
  //#endregion

  //#region validate file
  const validateFileExtension = useCallback((file: File) => {
    const fileExtension = `.${getFileExtension(file.name)}`;
    return (
      acceptedAudioFileExtensions.includes(fileExtension) ||
      acceptedImageFileExtensions.includes(fileExtension) ||
      acceptedTextFileExtensions.includes(fileExtension) ||
      acceptedVideoFileExtensions.includes(fileExtension)
    );
  }, []);
  //#endregion

  //#region set new files
  const handleSetNewFiles = useCallback(
    async (files: FileList | null) => {
      let newIFiles: IFile[] = [];
      let newALFiles: CustomFiles[] = [];

      if (files) {
        for (const file of files) {
          if (validateFileExtension(file)) {
            if (await checkFileMimeType(file)) {
              newIFiles.push({
                file,
                fileName: file.name.split(".").slice(0, -1).join("."),
                fileExtension: getFileExtension(file.name),
              });

              const base64Content = await convertBlobToBase64(file);
              newALFiles.push({
                alertLineReport: { id: state.alertLineReport.id } as AlertLineReportRef,
                isDeleted: false,
                id: uuid(),
                file: base64Content,
                fileName: file.name,
                fileType: getFileExtension(file.name),
                isDescription: false,
                isFileMalicious: false,
                blob: file,
              });
            } else {
              window.alert(`Corrupted file`);
            }
          } else {
            window.alert(`File type ${getFileExtension(file.name)} not supported`);
          }
        }

        setState((state) => ({
          ...state,
          files: [...state.files, ...newIFiles],
          filesToStore: [...state.filesToStore, ...newALFiles],
        }));
      }
    },
    [state.alertLineReport]
  );
  //#endregion

  //#region open file picker
  const handleAddFile = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);
  //#endregion

  //#region delete file
  const handleDeleteFile = useCallback(
    (fileId: string) => {
      const confirm = window.confirm(`${t(`${tRP}.delete_file_message`)}`);
      if (confirm) {
        setState((state) => ({
          ...state,
          filesToStore: state.filesToStore.filter((file) => file.id !== fileId),
        }));
      }
    },
    [t, state.files, state.alertLineReport.files]
  );
  //#endregion

  //#region handle vocal description
  const handleAudio = useCallback(
    (audioURL: string, audioBlob: Blob) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64Audio = reader.result as string;
        if (base64Audio) {
          const base64AudioContent = base64Audio.split(",")[1];
          const vocalFile = {
            isDeleted: false,
            id: uuid(),
            alertLineReport: { id: state.alertLineReport.id } as AlertLineReportRef,
            fileName: "vocal_message.wav",
            fileType: "wav",
            file: base64AudioContent,
            isDescription: true,
            blob: new File([audioBlob], "vocal_message.wav", { lastModified: Date.now() }),
            audioSrc: URL.createObjectURL(audioBlob),
          } as CustomFiles;

          setState((state) => ({
            ...state,
            vocalDescriptionURL: [
              ...state.vocalDescriptionURL,
              {
                url: audioURL,
                fileId: vocalFile.id,
              },
            ],
            filesToStore: [...state.filesToStore, vocalFile],
          }));
        }
      };

      reader.readAsDataURL(audioBlob);
    },
    [state.alertLineReport]
  );
  //#endregion

  //#region delete vocal description
  const handleDeleteAudio = useCallback(
    (fileId: string) => {
      const confirm = window.confirm(`${t(`${tRP}.delete_vocal_desription`)}`);
      if (confirm) {
        setState((state) => ({
          ...state,
          vocalDescriptionURL: [
            ...state.vocalDescriptionURL.filter((vocalDescription) => vocalDescription.fileId !== fileId),
          ],
          filesToStore: state.filesToStore.filter((file) => file.id !== fileId),
          alertLineReport: {
            ...state.alertLineReport,
            files: [...state.alertLineReport.files.filter((file) => file.id !== fileId)],
          },
        }));
      }
    },
    [t]
  );
  //#endregion

  //#region validate fields
  const areAllFieldsValidated = useCallback(() => {
    return (
      state.alertLineReport.subject.trim().length > 0 &&
      !!state.alertLineReport.violationClassification &&
      state.alertLineReport.description.trim().length > 0 &&
      (state.alertLineReport.addressedToHr ||
        state.alertLineReport.addressedToManagement ||
        state.alertLineReport.addressedToCompanyCommittee)
    );
  }, [state.alertLineReport]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      allFieldsValidated: areAllFieldsValidated(),
    }));
  }, [state.alertLineReport]);
  //#endregion

  //#region submit
  const handleSubmit = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const filesToSave = state.filesToStore
        .filter((_file) => !_file.isDescription)
        .map((_file) => {
          return {
            file: _file.blob,
            fileExtension: _file.fileType,
            fileName: _file.fileName,
          };
        });
      const vocalMessagesToSave = state.filesToStore
        .filter((_file) => _file.isDescription)
        .map((_file) => {
          return {
            file: _file.blob,
            fileExtension: _file.fileType,
            fileName: _file.fileName,
          };
        });

      const res =
        !!filesToSave.length || !!vocalMessagesToSave.length
          ? await saveDocs(!!filesToSave.length, !!vocalMessagesToSave.length)
          : null;

      if (res?.response?.error) {
        return;
      }

      if (res?.docOne || res?.docTwo) {
        if (res?.docOne && !res.docTwo) {
          await Promise.all([handleUploadNewFiles(res.docOne, filesToSave as any, null, null, true)]);
        } else if (res.docTwo && !res.docOne) {
          await Promise.all([handleUploadNewFiles(res.docTwo, vocalMessagesToSave as any, null, null, true)]);
        } else if (res.docTwo && res.docOne) {
          await Promise.all([
            handleUploadNewFiles(res.docOne, filesToSave as any, null, null, true),
            handleUploadNewFiles(res.docTwo, vocalMessagesToSave as any, null, null, true),
          ]);
        }
      }

      const response = await new AlertLineReportApiService().saveItems([
        {
          ...state.alertLineReport,
          files: [
            ...(!!res?.docOne
              ? [
                  {
                    id: uuid(),
                    isDeleted: false,
                    isConstant: false,
                    isDescription: false,
                    document: res.docOne,
                    alertLineReport: { id: state.alertLineReport.id },
                  },
                ]
              : []),
            ...(res?.docTwo
              ? [
                  {
                    id: uuid(),
                    isDeleted: false,
                    isConstant: false,
                    isDescription: true,
                    document: res.docTwo,
                    alertLineReport: { id: state.alertLineReport.id },
                  } as AlertLineReportFileRef,
                ]
              : []),
          ],
        },
      ]);
      if (!response.error) {
        setState((state) => ({ ...state, isPostSubmitView: true }));
        navigator.clipboard.writeText(state.alertLineReport.key);
      } else {
        setState((state) => ({ ...state, error: response.error }));
        const container = document.getElementById("alert-line-container");
        container?.scroll(0, 0);
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.alertLineReport]);
  //#endregion

  //#region set to inital state
  const setToInitialState = useCallback(
    (requiresConfirmation: boolean) => {
      handleChangeView(ALERT_LINE_PAGE.LandingPage, undefined, requiresConfirmation);
    },
    [handleChangeView, INITIAL_STATE]
  );
  //#endregion

  useEffect(() => {
    return () => setState({ ...INITIAL_STATE });
  }, []);

  useEffect(() => {
    setState((state) => ({
      ...state,
      alertLineReport: {
        ...state.alertLineReport,
        language: selectedLanguage?.language ?? null,
      },
    }));
  }, [selectedLanguage]);
  return (
    <>
      {state.isPostSubmitView ? (
        <PostSubmitAlertLineReport alertLineReport={state.alertLineReport} />
      ) : (
        <>
          <LoadingOverlay loading={state.loading}>
            <Container id="alert-line-container">
              {state.error && <Alert severity="error">{state.error}</Alert>}
              <Field>
                <ReportTypeContainer
                  value={state.alertLineReport.anonymous ? "true" : "false"}
                  onChange={(e) => handleChange("anonymous", e.currentTarget.value === "true")}
                >
                  <Grid>
                    <FormControlLabel
                      value={"true"}
                      control={<Radio size="small" />}
                      label={`${t(`${tRP}.fields.report_anonymously`)}`}
                      style={{
                        color: theme.palette.secondary.light,
                      }}
                    />
                    <Grid>
                      <Typography>{`${t(`${tRP}.fields.report_anonymously_description`)}`}</Typography>
                    </Grid>
                  </Grid>
                  <Grid>
                    <FormControlLabel
                      value={"false"}
                      control={<Radio size="small" />}
                      label={`${t(`${tRP}.fields.report_not_anonymously`)}`}
                      style={{
                        color: theme.palette.secondary.light,
                      }}
                    />
                    <Grid>
                      <Typography>{`${t(`${tRP}.fields.report_not_anonymously_description`)}`}</Typography>
                    </Grid>
                  </Grid>
                </ReportTypeContainer>
              </Field>
              <Field>
                <Label>{`${t(`${tRP}.fields.subject`)}`}</Label>
                <TextField
                  value={state.alertLineReport.subject}
                  onChange={(e) => handleChange("subject", e.currentTarget.value)}
                />
              </Field>
              <Field>
                <Label>{`${t(`${tRP}.fields.category`)}`}</Label>
                <AutocompleteDropdown
                  serviceApi={new ViolationClassificationApiService()}
                  selectedValue={state.alertLineReport.violationClassification}
                  field={"violationClassification"}
                  handleChange={handleChange}
                  propsFilter={
                    {
                      alertLineURLId: alertLineURL.id,
                      languageId: selectedLanguage?.language?.id,
                    } as ViolationClassificationFilter
                  }
                />
              </Field>
              <Field>
                <AddressedToContainer>
                  <AddressToField>
                    <FormControlLabel
                      label={`${t(`${tRP}.fields.adress_to_hr`)}`}
                      control={
                        <Checkbox
                          checked={state.alertLineReport.addressedToHr}
                          onChange={(e) => handleChange("addressedToHr", e.currentTarget.checked)}
                        />
                      }
                    />
                    {/* <UserDesk /> */}
                  </AddressToField>
                  <AddressToField>
                    <FormControlLabel
                      label={`${t(`${tRP}.fields.address_to_manager`)}`}
                      control={
                        <Checkbox
                          checked={state.alertLineReport.addressedToManagement}
                          onChange={(e) => handleChange("addressedToManagement", e.currentTarget.checked)}
                        />
                      }
                    />
                    {/* <UserWithTie /> */}
                  </AddressToField>
                  <AddressToField>
                    <FormControlLabel
                      label={`${t(`${tRP}.fields.address_to_company_committee_members`)}`}
                      control={
                        <Checkbox
                          checked={state.alertLineReport.addressedToCompanyCommittee}
                          onChange={(e) => handleChange("addressedToCompanyCommittee", e.currentTarget.checked)}
                        />
                      }
                    />
                    {/* <BuildingWithUsers /> */}
                  </AddressToField>
                </AddressedToContainer>
              </Field>

              {!state.alertLineReport.anonymous && (
                <>
                  <Field>
                    <Label>{`${t(`${tRP}.fields.full_name`)}`}</Label>
                    <TextField
                      value={state.alertLineReport.fullName}
                      onChange={(e) => handleChange("fullName", e.currentTarget.value)}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.fields.email`)}`}</Label>
                    <TextField
                      value={state.alertLineReport.email}
                      onChange={(e) => handleChange("email", e.currentTarget.value)}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.fields.phone_number`)}`}</Label>
                    <TextField
                      value={state.alertLineReport.phoneNumber}
                      onChange={(e) => handleChange("phoneNumber", e.currentTarget.value)}
                    />
                  </Field>
                </>
              )}

              <DescriptionLabelContainer>
                <Label>{`${t(`${tRP}.fields.description`)}`}</Label>
              </DescriptionLabelContainer>
              <DescriptionBlockContainer>
                <UploadFilesContainer>
                  <SmallLabel>{`${t(`${tRP}.fields.files`)}`}</SmallLabel>
                  <IconButton onClick={handleAddFile}>
                    <AttachIcon
                      color={theme.palette.primary.main}
                      width="35px"
                      height="35px"
                    />
                  </IconButton>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }} // Hide the actual file input element
                    onChange={(e) => handleSetNewFiles(e.target.files)} // Handle file selection
                  />

                  {state.filesToStore.length > 0 && (
                    <UploadedFilesContainer>
                      {state.filesToStore.filter((file) => !file.isDescription).length === 0 && (
                        <Label>{`${t(`${tRP}.no_files_uploaded`)}`}</Label>
                      )}
                      {state.filesToStore
                        .filter((file) => !file.isDescription)
                        .map((_file, idx) => (
                          <SingleUploadedFile key={idx}>
                            <FileIconButton
                              onClick={() =>
                                setState((state) => ({
                                  ...state,
                                  fileToView: {
                                    file: _file.blob,
                                    fileName: _file.fileName,
                                    fileExtension: _file.fileType,
                                  },
                                }))
                              }
                              style={{ opacity: 1 }}
                            >
                              <FileIcon
                                extension={_file.fileType}
                                {...customFileIconStyles[_file.fileType as DefaultExtensionType]}
                                radius={3}
                              />
                            </FileIconButton>
                            <DeleteFileButton onClick={() => handleDeleteFile(_file.id)}>
                              <FontAwesomeIcon
                                icon={faTrash}
                                color={theme.palette.error.main}
                              />
                            </DeleteFileButton>
                          </SingleUploadedFile>
                        ))}
                    </UploadedFilesContainer>
                  )}
                </UploadFilesContainer>

                <RecordedAudioContainer>
                  <SmallLabel>{`${t(`${tRP}.fields.recorded_voice_messages`)}`}</SmallLabel>
                  <Grid style={{ display: "flex", padding: "0px 10px", gap: "5px", alignItems: "start" }}>
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, marginTop: "10px" }}
                    >
                      <i>{`${t(`${tRP}.no_filters`)}`}</i>
                    </Typography>
                    <VoiceRecorder
                      handleAudio={handleAudio}
                      onlyIcon
                      customButtonStyles={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        color: theme.palette.primary.main,
                        fontSize: "24px",
                        padding: "0px",
                      }}
                    />
                  </Grid>

                  <RecordedAudioList>
                    {state.filesToStore
                      .filter((file) => file.isDescription)
                      .map((file, idx) => {
                        return (
                          <SingleRecordedAudioContainer
                            style={{ display: "flex" }}
                            key={idx}
                          >
                            <audio
                              key={idx}
                              controls
                              src={file?.audioSrc ?? ""}
                              preload="auto"
                            />
                            <DeleteFileButton onClick={() => handleDeleteAudio(file.id)}>
                              <FontAwesomeIcon
                                icon={faTrash}
                                color={theme.palette.error.main}
                              />
                            </DeleteFileButton>
                          </SingleRecordedAudioContainer>
                        );
                      })}
                  </RecordedAudioList>
                </RecordedAudioContainer>

                <DescriptionContainer>
                  <TextField
                    value={state.alertLineReport.description}
                    onChange={(e) => handleChange("description", e.currentTarget.value)}
                    multiline
                    minRows={6}
                    style={{
                      width: "100%",
                    }}
                  />
                </DescriptionContainer>
              </DescriptionBlockContainer>

              <Button
                variant="contained"
                onClick={handleSubmit}
                style={{ width: "170px", alignSelf: "end", pointerEvents: "all" }}
                disabled={!state.allFieldsValidated}
                title={
                  state.alertLineReport.violationClassification
                    ? `${t(`${tRP}.submit`)}`
                    : `${t(`${tRP}.fill_all_fields`)}`
                }
              >
                {`${t(`${tRP}.submit`)}`}
              </Button>
            </Container>
          </LoadingOverlay>

          <DocumentViewerContainer open={!!state.fileToView}>
            <DialogTitle
              title=""
              handleClose={() => {
                setState((state) => ({ ...state, fileToView: null }));
              }}
            />
            <Grid style={{ width: "90vw" }}>
              {state.fileToView && (
                <DocumentViewer
                  file={state.fileToView}
                  withFooter
                />
              )}
            </Grid>
          </DocumentViewerContainer>

          <Dialog
            open={state.dialogKeyOpen}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              title={`${t(`${tRP}.alert_report_key`)}`}
              handleClose={() => setToInitialState(false)}
            />
            <LoadingOverlay loading={state.exportLoading}>
              <DialogContent style={{ display: "flex", flexDirection: "column", gap: "10px", flexWrap: "wrap" }}>
                <Alert severity="success">{`${t(`${tRP}.report_saved_successfully`)}`}</Alert>
                <Typography>
                  {`${t(`${tRP}.use_key_message`)}`}
                  &nbsp;
                  <b>{state.alertLineReport.key}</b> (copied to clipboard) &nbsp;&nbsp;
                </Typography>
              </DialogContent>
              <DialogActions style={{ padding: "20px" }}>
                <Button
                  variant="contained"
                  onClick={() => setToInitialState(false)}
                >
                  {`${t(`common_words.okay`)}`}
                </Button>
              </DialogActions>
            </LoadingOverlay>
          </Dialog>
        </>
      )}
    </>
  );
};
export default AddAlertLineReport;
